import type { AsyncDataOptions, NuxtApp } from '#app';

export function alwaysGetCached(key: string, nuxtApp: NuxtApp, ctx: { cause: string }) {
  // Only return cached data on the initial call, not on explicit refreshes.
  if (ctx.cause !== 'initial') {
    return undefined;
  }

  // Never read `nuxtApp._asyncData` here: on a reactive key change Nuxt pre-fills the new key's
  // entry with the previous key's data before asking for cached data, so it would serve another
  // key's payload and suppress the fetch. Writers keep `payload.data` in sync instead.
  return nuxtApp.payload.data[key] || nuxtApp.static.data[key];
}

type ResponseTimestamped<T> = T & { fetchedAt: number };

export function getCachedTtl<T>(ttl: number) {
  return {
    transform: (data) => ({ ...data, fetchedAt: Date.now() }),
    getCachedData(key: string, nuxtApp: NuxtApp, ctx: { cause: string }) {
      if (ctx.cause !== 'initial') return undefined;

      const cachedData: ResponseTimestamped<T> | undefined = nuxtApp.payload.data[key] || nuxtApp.static.data[key];

      if (cachedData?.fetchedAt) {
        const isExpired = (Date.now() - cachedData.fetchedAt) > ttl;
        if (!isExpired) {
          return cachedData;
        }
      }
      return undefined;
    },
  } satisfies AsyncDataOptions<T>;
}